import { Mail, Phone, MapPin, Building2, ArrowRight } from 'lucide-react';
import { Card } from '@/components/ui/card';

export const DemoSection = () => {
  const contactInfo = [
    {
      icon: Mail,
      label: 'Email',
      value: 'support@hirerkey.com',
      href: 'mailto:support@hirerkey.com',
    },
    {
      icon: Phone,
      label: 'Call',
      value: '+91 9711 298 777',
      href: 'tel:+919711298777',
    },
    {
      icon: Building2,
      label: 'Company',
      value: 'KSM Emirates Group LLC FZ',
    },
    {
      icon: MapPin,
      label: 'Address',
      value: 'Meydan Grandstand, Meydan Road, Nad Al Sheba, Dubai, UAE',
    },
  ];

  return (
    <section className="py-8 sm:py-10 md:py-12 bg-gradient-to-b from-background via-muted/30 to-background relative overflow-hidden">
      {/* Futuristic Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-40">
        <div className="absolute top-0 left-1/4 w-px h-full bg-gradient-to-b from-transparent via-primary/20 to-transparent"></div>
        <div className="absolute top-0 left-1/2 w-px h-full bg-gradient-to-b from-transparent via-primary/30 to-transparent"></div>
        <div className="absolute top-0 left-3/4 w-px h-full bg-gradient-to-b from-transparent via-primary/20 to-transparent"></div>
        <div className="absolute top-1/2 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary/20 to-transparent"></div>
      </div>

      <div className="relative max-w-[745px] mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-10 animate-fade-in">
          <div className="inline-block px-3 py-1 rounded-full bg-primary/10 border border-primary/20 mb-4">
            <span className="text-xs font-medium text-primary">Get in Touch</span>
          </div>
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-3 sm:mb-4">
            Ready to <span className="emphasize">Reimagine Your Workplace</span>?
          </h2>
          <p className="text-sm sm:text-base text-muted-foreground leading-relaxed max-w-2xl mx-auto">
            Connect with our team to discover how hirerkey<sup>™</sup> can <span className="emphasize-accent font-semibold text-foreground">enhance your workplace experience</span>.
          </p>
        </div>

        {/* Modern Contact Grid */}
        <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 mb-8 sm:mb-10">
          {contactInfo.map((item, index) => {
            const Icon = item.icon;
            const isClickable = !!item.href;
            const content = (
              <>
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-to-br from-primary/20 to-primary/10 border border-primary/20 flex items-center justify-center">
                    <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">
                      {item.label}
                    </p>
                    <p className={`text-xs sm:text-sm font-medium break-words ${isClickable ? 'text-primary group-hover:text-primary/80' : 'text-foreground'}`}>
                      {item.value}
                    </p>
                  </div>
                  {isClickable && (
                    <ArrowRight className="w-4 h-4 text-primary opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 mt-6" />
                  )}
                </div>
              </>
            );

            return isClickable ? (
              <a
                key={index}
                href={item.href}
                className="group block p-4 sm:p-5 rounded-lg bg-card/50 backdrop-blur-sm border border-border/50 hover:border-primary/30 hover:bg-card/80 transition-all animate-fade-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                {content}
              </a>
            ) : (
              <div
                key={index}
                className="p-4 sm:p-5 rounded-lg bg-card/50 backdrop-blur-sm border border-border/50 animate-fade-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                {content}
              </div>
            );
          })}
        </div>

        {/* Bottom Info Bar */}
        <div className="relative inline-block w-full">
          {/* Background layer */}
          <div className="absolute inset-0 bg-dark-section rounded-lg translate-x-0.5 translate-y-0.5"></div>
          
          {/* Main card */}
          <Card className="relative p-4 bg-gradient-to-r from-blue-500/10 via-purple-500/10 to-teal-500/10 border border-white/10 backdrop-blur-sm animate-fade-in">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-2.5 sm:gap-3">
              <div className="flex items-center space-x-2.5 sm:space-x-3">
                <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xs font-bold text-white">Quick Response Time</h3>
                  <p className="text-[10px] text-gray-300">
                    We respond within 24 hours on business days
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-1.5 text-[10px] text-gray-300">
                <div className="w-1.5 h-1.5 rounded-full bg-success animate-pulse"></div>
                <span>Available now</span>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default DemoSection;
